import { useContext, useEffect, useState } from 'react';
import { DraftActionsContainer, StyledDrawerContent } from './styled';
import { Dropdown, notification, theme, Modal, Drawer } from 'antd';
import { useMeasurement } from '@/hooks/useMeasurement';
import { useStores } from '@/hooks/use-stores';
import IconChevronDown from '@/assets/icon-chevron-down.svg?react';
import { EngineTab } from '@/constants';
import { observer } from 'mobx-react';
import { t } from 'i18next';
import { useNavigate } from 'react-router-dom';
import { EPButton } from '@/components';
import { useIsMobile } from '@/hooks/useIsMobile';
import { DrawerContext } from '@/context/DrawerContext';
import {
  getMenuItems,
  handleDiscardChanges,
  handlePreSaveChanges,
  handlePublish,
  handleRevertToLastPublished,
  validateCompressorParameters,
  validateStationParameters,
  versionTabs,
} from './CentrifugalCompressors.utils';

const { useToken } = theme;

const DraftActions = observer(({ setActiveTab, isSaveDisabled, setIsSaveDisabled, setActiveCentrifugalTab }) => {
  const { engineStore } = useStores();
  const { token } = useToken();
  const { isMobile } = useIsMobile();
  const { showDrawer, setShowDrawer } = useContext(DrawerContext);
  const { tvalue } = useMeasurement();
  const navigate = useNavigate();
  const [api, contextHolder] = notification.useNotification();

  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingPublish, setIsLoadingPublish] = useState(false);
  const [isLoadingRevert, setIsLoadingRevert] = useState(false);
  const [disablePublish, setDisablePublish] = useState(true);
  const [openPublishModal, setOpenPublishModal] = useState(false);
  const [openRevertModal, setOpenRevertModal] = useState(false);

  const stationParameter = engineStore.stationConfiguration && engineStore.stationConfiguration.stationParameter;

  const compressorParameters =
    engineStore.compressorsConfiguration && engineStore.compressorsConfiguration.compressorParameters;

  const flowSensorsPriority = engineStore.sensorsConfiguration && engineStore.sensorsConfiguration.flowSensorsPriority;

  const pressureSensorsPriority =
    engineStore.sensorsConfiguration && engineStore.sensorsConfiguration.pressureSensorsPriority;

  useEffect(() => {
    if (engineStore.hasChanges()) {
      setIsSaveDisabled(false);
    } else {
      setIsSaveDisabled(true);
    }
  }, [stationParameter, compressorParameters, flowSensorsPriority, pressureSensorsPriority]);

  useEffect(() => {
    setDisablePublish(!engineStore.hasDraftVersion);
  }, [engineStore.hasDraftVersion]);

  const validateCompressorParametersHandler = () => {
    return validateCompressorParameters(
      compressorParameters,
      setIsSaveDisabled,
      setIsLoading,
      api,
      setActiveCentrifugalTab,
      EngineTab,
    );
  };

  const validateStationParametersHandler = () => {
    return validateStationParameters(
      stationParameter,
      setIsSaveDisabled,
      setIsLoading,
      api,
      setActiveCentrifugalTab,
      EngineTab,
    );
  };

  const onPreSaveChanges = async () => {
    await handlePreSaveChanges(
      engineStore,
      tvalue,
      setIsLoading,
      setIsSaveDisabled,
      setDisablePublish,
      api,
      validateStationParametersHandler,
      validateCompressorParametersHandler,
    );
  };

  const onRevertToLastPublished = async () => {
    await handleRevertToLastPublished(engineStore, tvalue, setIsLoadingRevert, navigate, api);
  };

  const onDiscardChanges = async () => {
    handleDiscardChanges(isMobile, setShowDrawer, engineStore);
  };

  const onPublish = async () => {
    await handlePublish(
      validateCompressorParametersHandler,
      validateStationParametersHandler,
      setOpenPublishModal,
      setIsLoadingPublish,
      isMobile,
      setShowDrawer,
      engineStore,
      tvalue,
      api,
      setActiveTab,
      versionTabs,
    );
  };

  const items = getMenuItems(setOpenPublishModal, setOpenRevertModal, onDiscardChanges, engineStore, disablePublish);

  const renderActionsDesktop = () => (
    <>
      <Dropdown trigger={['hover']} menu={{ items }} placement="bottomRight">
        <div className="more-actions">
          {t('moreActions')} <IconChevronDown />
        </div>
      </Dropdown>
      <EPButton
        className="save-button"
        loading={isLoading}
        disabled={engineStore.isReadOnly || !engineStore.hasChanges() || isSaveDisabled}
        type="primary"
        onClick={onPreSaveChanges}
      >
        {t('save')}
      </EPButton>
    </>
  );

  const renderActionsMobile = () => (
    <>
      <EPButton
        style={{ width: '100%' }}
        className="save-button"
        loading={isLoading}
        disabled={engineStore.isReadOnly || !engineStore.hasChanges() || isSaveDisabled}
        type="primary"
        onClick={onPreSaveChanges}
      >
        {t('save')}
      </EPButton>

      <Dropdown trigger={['click']} menu={{ items }} placement="bottomRight">
        <EPButton
          style={{ width: '100%' }}
          className="more-actions-button"
          type="default"
          icon={<IconChevronDown />}
          disabled={engineStore.isReadOnly}
        >
          {t('moreActions')}
        </EPButton>
      </Dropdown>
    </>
  );

  return (
    <DraftActionsContainer>
      {contextHolder}
      {!isMobile && <DraftActionsContainer>{renderActionsDesktop()}</DraftActionsContainer>}

      {isMobile && (
        <>
          <DraftActionsContainer style={{ display: 'none' }}>{renderActionsDesktop()}</DraftActionsContainer>

          <Drawer placement="bottom" onClose={() => setShowDrawer(false)} height={200} open={showDrawer}>
            <StyledDrawerContent>{renderActionsMobile()}</StyledDrawerContent>
          </Drawer>
        </>
      )}

      <Modal
        title={t('confirmPublish')}
        open={openPublishModal}
        onOk={onPublish}
        okText={t('publishChange')}
        cancelText={t('reviewAgain')}
        onCancel={() => setOpenPublishModal(false)}
        centered
        width={550}
        closable={true}
        maskTransitionName=""
        transitionName=""
        confirmLoading={isLoadingPublish}
        style={{ backgroundColor: token.colorBgContainer }}
        okButtonProps={{ style: { borderColor: token.colorBgContainer } }}
      >
        <span style={{ color: token.colorBorder }}>{t('publishConfirmation')}</span>
      </Modal>

      <Modal
        open={openRevertModal}
        onOk={onRevertToLastPublished}
        onCancel={() => setOpenRevertModal(false)}
        okText={t('yes')}
        cancelText={t('cancel')}
        centered
        confirmLoading={isLoadingRevert}
        title={t('revertToLastPublished')}
        maskClosable={true}
        closable={true}
        maskTransitionName=""
        transitionName=""
      >
        <span>{t('revertPublishedVersion')}</span>
      </Modal>
    </DraftActionsContainer>
  );
});

export default DraftActions;
